import { PencilSquareIcon, PlusCircleIcon, XCircleIcon } from '@heroicons/react/20/solid'
import { Site } from './types'
import { LanguageCode } from './types/invoice'

export const UAT_STORAGE_KEY = 'userAccessToken'

export const DESKTOP_MIN_WIDTH = 1024
export const MOBILE_BREAKPOINT = `${DESKTOP_MIN_WIDTH}px`

export const REGISTER_USER_AGREEMENT = [
  `Osobne podatke koje nam dajete na raspolaganje prikupljamo, obrađujemo ili koristimo kako
  bismo odgovorili na Vaše upite ili Vam osigurali ostvarivanje vaših prava
  temeljem zakonskih propisa.`,
  `Prikupljanje, obrada i korištenje osobnih podataka kao i prosljeđivanje istih ovlaštenim
  državnim institucijama i ustanovama uslijedit će samo u okviru važećih zakona Opće uredbe o
  zaštiti osobnih podataka, odnosno ako smo to dužni učiniti na temelju sudske odluke.`,
  'Vaši osobni podaci neće se davati trećim osobama niti se na drugi način stavljati na tržište.'
]

// eslint-disable-next-line
export const FOOTER_TAGLINE = 'Omogućavamo freelancerima i malim poduzetnicima efikasnije poslovanje.'

export const accountTabs = [
  {
    title: 'Moj profil',
    url: '/account/profile/'
  },
  {
    title: 'Tvrtka',
    url: '/account/company/'
  },
  {
    title: 'Bankovni računi',
    url: '/account/bank-accounts/'
  },
  {
    title: 'Pravni podatci',
    url: '/account/legal-data/'
  },
  {
    title: 'Prom. lozinke',
    url: '/account/change-password/'
  },
]

export const ROUTES = {
  HOME: '/',
  NOT_FOUND: '/404/',
  INTERNAL_ERROR: '/500/',

  // Account
  LOGIN: '/account/login/',
  LOGOUT: '/account/logout/',
  REGISTER: '/account/register/',
  REGISTER_SUCCESS: '/account/register-success/',
  PROFILE: '/account/profile/',
  COMPANY: '/account/company/',
  BANK_ACCOUNTS: '/account/bank-accounts/',
  LEGAL_DATA: '/account/legal-data/',
  CHANGE_PASSWORD: '/account/change-password/',
  ONBOARDING: '/account/onboarding/',
  RESET_PASSWORD_REQUEST: '/account/reset-password/request/',
  RESET_PASSWORD: (userId: string, verificationToken: string) => (
    `/account/reset-password/${userId}/${verificationToken}/`
  ),
  ACTIVATE_REQUEST: '/account/activate/request/',
  ACTIVATE: (userId: string, activationCode: string) => (
    `/account/activate/${userId}/${activationCode}/`
  ),

  // Subscription
  BILLING: '/billing/',
  SELECT_PLAN: '/billing/select-plan/',
  CONFIRM_PLAN: '/billing/confirm-plan/',
  PAYMENT_CALLBACK: '/billing/payment-callback/',
  ADD_PAYMENT_METHOD: '/billing/add-payment-method/',

  // Counter parties
  COUNTER_PARTY_EDIT: (id: string) => `/counter-parties/${id}/edit/`,
  COUNTER_PARTY_ADD: '/counter-parties/add/',
  COUNTER_PARTY_LIST: '/counter-parties/',

  // Invoices
  INVOICE_LIST: '/invoices/',
  INVOICE_ADD: '/invoices/add/',
  INVOICE_EDIT: (id: string) => `/invoices/${id}/edit/`,

  // Invoice templates
  INVOICE_TEMPLATE_EDIT: (id: string) => `/invoice-templates/${id}/edit/`,
  INVOICE_TEMPLATE_ADD: '/invoice-templates/add/',
  INVOICE_TEMPLATE_LIST: '/invoice-templates/',

  // Marketing
  FAQ: '/faq/',
  PRICING: '/pricing/',
  PRICINT_TEST: '/pricing-2/',
  ROADMAP: '/roadmap/',
  CONTACT: '/contact/',

  // Legal
  TERMS: '/terms-of-use/',
  PRIVACY: '/privacy-policy/',

  // Blog
  BLOG_LIST: '/blog/',
  BLOG_DETAIL: (slug: string) => `/blog/${slug}/`,

  // Reports
  REPORT_LIST: '/reports/',
  REPORT_ADD: '/reports/add/',
  REPORT_EDIT: (id: string) => `/reports/${id}/edit/`,

  // Incoming invoices
  INCOMING_INVOICE_LIST: '/incoming-invoices/',
  INCOMING_INVOICE_ADD: '/incoming-invoices/add/',
  INCOMING_INVOICE_EDIT: (id: string) => `/incoming-invoices/${id}/edit/`,

  // Statements
  STATEMENT_LIST: '/statements/',
  STATEMENT_DETAIL: (id: string) => `/statements/${id}/`,
  STATEMENT_ADD: '/statements/add/',
  STATEMENT_REVIEW: '/statements/review/',

  // Test routes
  PRICING_TEST: '/pricing-2/',
}

export const siteMapExcludePaths = [
  ROUTES.INVOICE_LIST,
  ROUTES.INVOICE_ADD,
  ROUTES.INVOICE_EDIT('[invoiceId]'),
  ROUTES.INVOICE_TEMPLATE_LIST,
  ROUTES.INVOICE_TEMPLATE_ADD,
  ROUTES.INVOICE_TEMPLATE_EDIT('[templateId]'),
  ROUTES.REGISTER_SUCCESS,
  ROUTES.LOGOUT,
  ROUTES.COUNTER_PARTY_LIST,
  ROUTES.COUNTER_PARTY_ADD,
  ROUTES.COUNTER_PARTY_EDIT('[counterPartyId]'),
  ROUTES.PROFILE,
  ROUTES.COMPANY,
  ROUTES.BANK_ACCOUNTS,
  ROUTES.LEGAL_DATA,
  ROUTES.CHANGE_PASSWORD,
  ROUTES.ONBOARDING,
  ROUTES.RESET_PASSWORD_REQUEST,
  ROUTES.RESET_PASSWORD('[userId]', '[verificationToken]'),
  ROUTES.ACTIVATE_REQUEST,
  ROUTES.ACTIVATE('[userId]', '[activationCode]'),

  // Billing routes
  ROUTES.BILLING,
  ROUTES.PRICING_TEST,
  ROUTES.SELECT_PLAN,
  ROUTES.CONFIRM_PLAN,
  ROUTES.PAYMENT_CALLBACK,
  ROUTES.ADD_PAYMENT_METHOD,

  // Reports routes
  ROUTES.REPORT_LIST,
  ROUTES.REPORT_ADD,
  ROUTES.REPORT_EDIT('[reportId]'),

  // Incoming invoices
  ROUTES.INCOMING_INVOICE_LIST,
  ROUTES.INCOMING_INVOICE_ADD,
  ROUTES.INCOMING_INVOICE_EDIT('[id]'),

  // Statements
  ROUTES.STATEMENT_LIST,
  ROUTES.STATEMENT_ADD,
  ROUTES.STATEMENT_DETAIL('[id]'),
  ROUTES.STATEMENT_REVIEW,
]

export const PAGES_SEO = {
  HOME: (site: Site) => ({
    title: `Jednostavno izdavanje računa uz ${site.siteMetadata.productName}`,
    // eslint-disable-next-line max-len
    description: `Kreirajte račune brzo i jednostavno. Ako ste freelancer ili mali poduzetnik ${site.siteMetadata.productName} je dizajniran za vas. Registrirajte se besplatno i fakturirajte odmah.`
  }),
  PRICING: (site: Site) => ({
    title: 'Paketi i cijene',
    // eslint-disable-next-line max-len
    description: `Pregledajte pakete na ${site.siteMetadata.productName}: besplatni Lajt idealan za paušalce i mikro firme ili Pro za napredne opcije. Odaberite idealno rješenje za brzo fakturiranje.`
  }),
  PRIVACY_POLICY: {
    title: 'Politika privatnosti',
    // eslint-disable-next-line max-len
    description: 'Pročitajte našu politiku privatnosti kako biste saznali kako štitimo vaše osobne podatke i čuvamo vašu privatnost prilikom korištenja naših usluga.'
  },
  ROADMAP: {
    title: 'Roadmap',
    // eslint-disable-next-line max-len
    description: 'Otkrijte što budućnost nosi za naše korisnike uz naš roadmap. Planirajte zajedno s nama i budite u toku s nadolazećim funkcionalnostima i poboljšanjima.'
  },
  TERMS_OF_USE: {
    title: 'Uvjeti korištenja',
    // eslint-disable-next-line max-len
    description: 'Upoznajte se s uvjetima korištenja kako biste osigurali informirano i sigurno iskustvo korištenja naših usluga. Pročitajte detaljno naše smjernice i pravila.'
  },
  FAQ: {
    title: 'Često postavljana pitanja',
    // eslint-disable-next-line max-len
    description: 'Pronađite odgovore na često postavljana pitanja i dobijte brze informacije o našim uslugama na stranici FAQ. Riješite svoje dileme o korištenju aplikacije.'
  },
  CONTACT: {
    title: 'Pošaljite nam svoj upit',
    // eslint-disable-next-line max-len
    description: 'Kontaktirajte nas za sve upite ili podršku. Tu smo da vam pomognemo sa svim upitima vezanim za naše usluge. Dostupni smo putem emaila ili kontakt formulara.'
  },
  NOT_FOUND: {
    title: '404: Stranica ne postoji',
    description: 'Stranica nije pronađena.'
  },
  SERVER_ERROR: {
    title: '500: Greška na serveru',
    description: 'Greška na serveru'
  },
  ACTIVATE_ACCOUNT: {
    title: 'Aktivacija korisničkog računa',
    description: 'Aktivacija korisničkog računa'
  },
  ACTIVATE_ACCOUNT_REQUEST: {
    title: 'Zahtjev za slanje aktivacijskog emaila',
    // eslint-disable-next-line max-len
    description: 'Kako biste aktivirali svoj korisnički račun, unesite email adresu kada ste ga kreirali.'
  },
  RESET_PASSWORD: {
    title: 'Reset izgubljene lozinke',
    description: 'Reset izgubljene lozinke'
  },
  RESET_PASSWORD_REQUEST: {
    title: 'Zahtjev za reset lozinke',
    // eslint-disable-next-line max-len
    description: 'Kako biste započeli postupak zamjene izgubljene lozinke, unesite email adresu koju ste koristili za kreiranje korisničkog računa.'
  },
  LOGIN: (site: Site) => ({
    title: `Prijavite se na ${site.siteMetadata.productName}`,
    // eslint-disable-next-line max-len
    description: 'Prijavite se za pristup svom korisničkom računu i otključajte sve mogućnosti naše platforme. Brz i siguran login čeka na vas za neometano korištenje usluga.'
  }),
  REGISTER: (site: Site) => ({
    title: `Registrirajte se na ${site.siteMetadata.productName}`,
    // eslint-disable-next-line max-len
    description: 'Registrirajte se danas i započnite koristiti naše usluge bez odgode. Postupak je brz i jednostavan, osiguravajući vam pristup svim našim funkcionalnostima.'
  }),
  REGISTER_SUCCESS: {
    title: 'Uspješna registracija',
    // eslint-disable-next-line max-len
    description: 'Pronađite aktivacijski email u vašem inboxu. Klikom na poveznicu aktivirajte svoj korisnički račun i započnite s korištenjem naše platforme.'
  },
  BLOG: (site: Site) => ({
    title: `Blog | ${site.siteMetadata.productName}`,
    // eslint-disable-next-line max-len
    description: 'Cilj nam je da na ovim stranicama pronađete ćete odgovore na sva pitanja vezana za vođenje obrta ili firme.'
  }),
  SELECT_PLAN: {
    title: 'Odabir paketa',
    description: `
      Odaberite paket koji najbolje odgovara vašim potrebama.
      Naša ponuda uključuje različite opcije kako biste pronašli idealno rješenje
      za svoje poslovanje.`
  },
  CONFIRM_PLAN: {
    title: 'Potvrda paketa',
    description: 'Potvrdite odabir paketa i nastavite sa aktivacijom.'
  },
  ADD_PAYMENT_METHOD: {
    title: 'Dodavanje kartice',
    description: 'Dodajte svoju karticu kako biste nastavili s plaćanjem.'
  },
  PAYMENT_CALLBACK: {
    title: 'Provjera plaćanja',
    description: 'Provjera plaćanja'
  },
}

export const DEFAULT_PAGE_SIZE = 25

export const COMMON_ACTION_ICONS = {
  ADD: {
    Icon: PlusCircleIcon,
    colorClassName: 'text-primary-600',
  },
  EDIT: {
    Icon: PencilSquareIcon,
    colorClassName: 'text-ink-muted',
  },
  DELETE: {
    Icon: XCircleIcon,
    colorClassName: 'text-brick',
  },
}

type LangStrings = {
  [key in LanguageCode]: Record<string, string>
}

export const langStrings: LangStrings = {
  HR: {
    Description: 'Opis',
    Note: 'Napomena',
  },
  EN: {
    Description: 'Description',
    Note: 'Note',
  }
}
